import React from "react";
import { Helmet } from "react-helmet";
import { sharedLegal } from "./legalContent";
import "assets/css/privacy-policy.css";

const languageStorageKey = "gabg-legal-language";
const supportedLanguages = ["en", "es"];

class LegalPage extends React.Component {
  state = {
    language: this.getInitialLanguage()
  };

  componentDidMount() {
    this.scrollToHashOrTop();
  }

  getInitialLanguage() {
    const storedLanguage = window.localStorage.getItem(languageStorageKey);

    if (supportedLanguages.includes(storedLanguage)) {
      return storedLanguage;
    }

    const browserLanguage = window.navigator.language.toLowerCase();
    return browserLanguage.startsWith("es") ? "es" : "en";
  }

  setLanguage = (language) => {
    window.localStorage.setItem(languageStorageKey, language);
    this.setState({ language }, () => {
      this.scrollToHashOrTop();
    });
  };

  scrollToHashOrTop() {
    if (window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));

      if (target) {
        window.requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }

      return;
    }

    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }

  renderSection(section, index) {
    return (
      <section className="privacy-card" id={section.id} key={`${section.title}-${index}`}>
        <div className="privacy-section-heading">
          <span className="privacy-section-number">{String(index + 1).padStart(2, "0")}</span>
          <div>
            <p className="privacy-eyebrow">{section.eyebrow}</p>
            <h2>{section.title}</h2>
          </div>
        </div>

        {section.paragraphs?.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}

        {section.grid && (
          <div className="privacy-grid">
            {section.grid.map((item) => (
              <article key={item.title}>
                <i className={`fa ${item.icon}`} aria-hidden="true" />
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        )}

        {section.checkList && (
          <ul className="privacy-check-list">
            {section.checkList.map((item) => (
              <li key={item}>
                <i className="fa fa-check" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        )}

        {section.bullets && (
          <ul className="privacy-bullet-list">
            {section.bullets.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}

        {section.afterParagraphs?.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}

        {section.services && (
          <div className="privacy-service-list">
            {section.services.map((service) => (
              <article key={service.title}>
                <h3>{service.title}</h3>
                <p>{service.copy}</p>
              </article>
            ))}
          </div>
        )}
      </section>
    );
  }

  render() {
    const { page } = this.props;
    const { language } = this.state;
    const labels = sharedLegal.labels[language];
    const content = page.content[language];
    const alternatePageKey = page.key === "privacy" ? "terms" : "privacy";

    return (
      <>
        <Helmet>
          <title>{page.title[language]}</title>
          <meta name="description" content={page.description[language]} />
        </Helmet>

        <main className="privacy-page">
          <header className="privacy-header">
            <a className="privacy-brand" href={sharedLegal.legalLinks[page.key]} aria-label={page.title[language]}>
              <span className="privacy-brand-mark">G</span>
              <span>
                <strong>{labels.brandTitle}</strong>
                <small>{labels.brandSubtitle}</small>
              </span>
            </a>

            <div className="privacy-header-actions">
              <nav className="privacy-page-links" aria-label="Legal pages">
                <a className={page.key === "privacy" ? "is-active" : ""} href={sharedLegal.legalLinks.privacy}>
                  {labels.privacy}
                </a>
                <a className={page.key === "terms" ? "is-active" : ""} href={sharedLegal.legalLinks.terms}>
                  {labels.terms}
                </a>
              </nav>

              <div className="privacy-language-switcher" aria-label={labels.language}>
                {supportedLanguages.map((languageOption) => (
                  <button
                    aria-pressed={language === languageOption}
                    className={language === languageOption ? "is-active" : ""}
                    key={languageOption}
                    onClick={() => this.setLanguage(languageOption)}
                    type="button"
                  >
                    {languageOption.toUpperCase()}
                  </button>
                ))}
              </div>

              <a className="privacy-header-link" href={`mailto:${sharedLegal.contactEmail}`}>
                <i className="fa fa-envelope-o" aria-hidden="true" />
                {labels.contact}
              </a>
            </div>
          </header>

          <section className="privacy-hero" id="overview">
            <div className="privacy-hero-content">
              <p className="privacy-kicker">{content.hero.kicker}</p>
              <h1>{content.hero.title}</h1>
              <p className="privacy-hero-copy">{content.hero.copy}</p>

              <div className="privacy-hero-actions">
                <a className="privacy-primary-action" href={`mailto:${sharedLegal.contactEmail}`}>
                  <i className="fa fa-paper-plane-o" aria-hidden="true" />
                  {sharedLegal.contactEmail}
                </a>
                <a className="privacy-secondary-action" href={sharedLegal.legalLinks[alternatePageKey]}>
                  {alternatePageKey === "terms" ? labels.terms : labels.privacy}
                </a>
                <span className="privacy-updated">{labels.lastUpdated}</span>
              </div>
            </div>

            <aside className="privacy-hero-panel" aria-label={content.hero.panelTitle}>
              <div className="privacy-panel-icon">
                <i className={`fa ${page.icon}`} aria-hidden="true" />
              </div>
              <h2>{content.hero.panelTitle}</h2>
              <p>{content.hero.panelCopy}</p>
            </aside>
          </section>

          <nav className="privacy-nav" aria-label={`${content.hero.kicker} sections`}>
            {content.nav.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>

          <section className="privacy-layout">
            <div className="privacy-content">
              {content.sections.map((section, index) => this.renderSection(section, index))}

              {content.contact && (
                <section className="privacy-contact-card" id="contact">
                  <div>
                    <p className="privacy-eyebrow">{content.contact.eyebrow}</p>
                    <h2>{content.contact.title}</h2>
                    <p>{content.contact.copy}</p>
                  </div>
                  <a className="privacy-primary-action" href={`mailto:${sharedLegal.contactEmail}`}>
                    <i className="fa fa-envelope-o" aria-hidden="true" />
                    {sharedLegal.contactEmail}
                  </a>
                </section>
              )}
            </div>
          </section>
        </main>

        <footer className="privacy-footer">
          <p>{sharedLegal.footer[language]}</p>
        </footer>
      </>
    );
  }
}

export default LegalPage;
